<template lang="pug">
.scan-product
    .page-loading(v-if="loading")
        el-skeleton(:rows="5" animated)

    template(v-else-if="product")
        el-button.scan-product__backlink(
            v-if="isArticle && parentProductId"
            icon="el-icon-arrow-left"
            @click="goToParentProduct"
        ) {{ $t('Zurück zum Produkt') }}

        .scan-product__head
            .scan-product__name {{ headTitle }}
            .scan-product__nr(v-if="headSubtitle") {{ headSubtitle }}

        .scan-product__galleryhead(v-if="visibleMedia.length > 1")
            el-button(
                type="text"
                size="small"
                :disabled="busy"
                @click="sortMode = !sortMode"
            )
                i(:class="sortMode ? 'el-icon-check' : 'el-icon-sort'")
                |  {{ sortMode ? $t('Fertig') : $t('Reihenfolge ändern') }}

        .scan-product__gallery(:class="{ sorting: sortMode }")
            .media-tile(
                v-for="(media, index) in visibleMedia"
                :key="media.id"
                :class="{ 'media-tile--editable': !sortMode }"
                @click="onTileClick(media)"
            )
                img(v-if="mediaUrl(media)" :src="mediaUrl(media)" :alt="product.name")
                i.el-icon-picture-outline(v-else)
                //- Rollen-Anzeige (read-only): Hauptbild / Listenbild / Produktseite
                .media-tile__rolebadges(v-if="!sortMode && hasAnyRole(media)")
                    i.el-icon-fa-star.fas(v-if="roleActive(media, 'highlight')")
                    i.el-icon-fa-bars.fas(v-if="roleActive(media, 'list')")
                    i.el-icon-fa-tag.fas(v-if="roleActive(media, 'detail')")
                template(v-if="!sortMode")
                    .media-tile__ai
                        i.el-icon-fa-wand-magic-sparkles.fas
                    button.media-tile__delete(
                        type="button"
                        :disabled="busy"
                        @click.stop="removeMedia(media)"
                    )
                        i.el-icon-fa-trash.fas
                //- Sortier-Modus: Bild eine Position nach oben/unten verschieben
                .media-tile__move(v-else)
                    button.move-btn(
                        type="button"
                        :disabled="busy || index === 0"
                        @click.stop="moveImage(media, -1)"
                    )
                        i.el-icon-arrow-up
                    button.move-btn(
                        type="button"
                        :disabled="busy || index === visibleMedia.length - 1"
                        @click.stop="moveImage(media, 1)"
                    )
                        i.el-icon-arrow-down

            .media-tile.media-tile--uploading(
                v-for="n in uploading"
                :key="'up-' + n"
            )
                i.el-icon-loading

            .scan-product__empty(v-if="!visibleMedia.length && !uploading")
                i.el-icon-picture-outline
                span {{ $t('Noch keine Bilder') }}

        .scan-product__actions
            input.hidden-input(
                ref="cameraInput"
                type="file"
                accept="image/*"
                capture="environment"
                @change="onFilesPicked"
            )
            input.hidden-input(
                ref="galleryInput"
                type="file"
                accept="image/*"
                multiple
                @change="onFilesPicked"
            )

            el-button.action-btn(
                type="primary"
                icon="el-icon-fa-camera fas"
                :disabled="busy"
                @click="openCamera"
            ) {{ $t('Foto aufnehmen') }}

            el-button.action-btn(
                icon="el-icon-fa-images far"
                :disabled="busy"
                @click="openGallery"
            ) {{ $t('Aus Galerie wählen') }}

            el-button.action-btn(
                icon="el-icon-picture-outline"
                :disabled="busy"
                @click="showLibraryDialog = true"
            ) {{ $t('Aus Mediathek') }}

        //- Artikel (Varianten) des Produkts – je Artikel eigene Bildverwaltung
        .scan-product__articles(v-if="!isArticle && (articlesLoading || articles.length)")
            .scan-product__articles-title {{ $t('Artikel') }}

            .articles-loading(v-if="articlesLoading")
                el-skeleton(:rows="3" animated)

            template(v-else)
                el-input.scan-product__articles-search(
                    v-if="articles.length > 1"
                    v-model="articleSearch"
                    :placeholder="$t('Artikel suchen…')"
                    clearable
                    prefix-icon="el-icon-search"
                    size="small"
                )
                .article-row(
                    v-for="article in filteredArticles"
                    :key="article.id"
                    @click="goToArticle(article)"
                )
                    .article-thumb
                        img(v-if="articleThumb(article)" :src="articleThumb(article)" :alt="article.sku")
                        i.el-icon-picture-outline(v-else)
                    .article-info
                        .article-name {{ articleAttributes(article) || article.name || article.sku || ('#' + article.id) }}
                        .article-sku(v-if="article.sku") {{ article.sku }}
                    .article-count
                        i.el-icon-fa-camera.fas
                        |  {{ articleImageCount(article) }}
                    i.el-icon-arrow-right.arrow

        image-library-dialog(
            :visible.sync="showLibraryDialog"
            :multiple="true"
            selection-type="image"
            @select="onLibrarySelected"
        )

        //- Kein upload-context → blendet "Bild ersetzen" aus; allow-library=false →
        //- blendet "Aus Mediathek wählen" aus. (Hochladen/Mediathek gibt es im Detail-View
        //- über die eigenen Aktions-Buttons.)
        generate-image-edit-drawer(
            :visible.sync="showImageDrawer"
            :product="product"
            :subject-type="subjectType"
            :source-image-id="imageEditSourceId"
            :allow-library="false"
            :show-role-toggles="true"
            @completed="onGenerationCompleted"
            @updated="onImageUpdated"
            @replaced="onImageReplaced"
            @update-image-alt="onImageAltUpdated"
            @update-image-ai-disclosure="onImageAiDisclosureUpdated"
            @update-image-ai-label-display="onImageAiLabelDisplayUpdated"
            @update-image-ai-source-type="onImageAiSourceTypeUpdated"
            @toggle-role="onToggleRole"
        )

    el-empty(v-else :description="$t('Nicht gefunden')")
</template>

<i18n>
{
  "de": {
    "Noch keine Bilder": "Noch keine Bilder",
    "Hauptbild": "Hauptbild",
    "Listenbild": "Listenbild",
    "Produktseite": "Produktseite",
    "Foto aufnehmen": "Foto aufnehmen",
    "Aus Galerie wählen": "Aus Galerie wählen",
    "Aus Mediathek": "Aus Mediathek",
    "KI-Bearbeitung": "KI-Bearbeitung",
    "Artikel": "Artikel",
    "Artikel suchen…": "Größe, Farbe, Name, SKU, GTIN …",
    "Zurück zum Produkt": "Zurück zum Produkt",
    "Reihenfolge ändern": "Reihenfolge ändern",
    "Fertig": "Fertig",
    "Nicht gefunden": "Nicht gefunden"
  }
}
</i18n>

<script>
import _ from 'lodash'
import uniqid from 'uniqid'
import { useSetupStore } from '@/stores'
import { ensureFreshToken } from '@/helpers'
import ProductApi from '@/extensions/shop/store/api/product'
import ArticleApi from '@/extensions/shop/store/api/article'
import ImageLibraryDialog from '@/components/Page/Sections/Partials/ImageLibraryDialog'
import GenerateImageEditDrawer from '@/extensions/generative-media/views/Drawers/GenerateImageEditDrawer'

const IMAGE_MIMES = ['image/jpeg', 'image/gif', 'image/png', 'image/webp']

export default {
    name: 'ScanProduct',
    components: { ImageLibraryDialog, GenerateImageEditDrawer },
    data() {
        return {
            product: null,
            loading: true,
            saving: false,
            uploading: 0,
            showLibraryDialog: false,
            showImageDrawer: false,
            imageEditSourceId: null,
            sortMode: false,
            articles: [],
            articlesLoading: false,
            articleSearch: '',
        }
    },
    computed: {
        setupStore() { return useSetupStore() },
        cmsId() { return this.setupStore.activeCms?.id },
        busy() { return this.saving || this.uploading > 0 },
        // Diese Ansicht dient Produkt UND Artikel — die Route entscheidet.
        isArticle() { return this.$route.name === 'scan-article' },
        subjectType() { return this.isArticle ? 'article' : 'product' },
        headTitle() {
            if (this.isArticle) return _.get(this.product, 'product.name') || this.product?.name || ''
            return this.product?.name || ''
        },
        parentProductId() { return _.get(this.product, 'product.id') },
        headSubtitle() {
            if (this.isArticle) {
                const sku = this.product?.sku
                return sku ? `SKU ${sku}` : (this.product?.name || '')
            }
            return this.product?.nr || ''
        },
        // Artikel-Upload geht an den Basis-Endpoint (context[type]=article steuert den Rest),
        // Produkt-Upload an die shop-Scope-Route — exakt wie die jeweiligen Desktop-Formulare.
        uploadEndpoint() {
            return this.isArticle
                ? this.setupStore.uploadEndpoint
                : `${this.setupStore.uploadEndpoint}/public/${this.cmsId}/shop`
        },
        // Artikel-Liste im Produkt filtern: Name, SKU, GTIN UND Attribute
        // (z.B. Größe „L", Farbe „Navy" – inkl. Eigenschaftsnamen).
        filteredArticles() {
            const term = this.articleSearch.trim().toLowerCase()
            if (!term) return this.articles
            return this.articles.filter(a => {
                const haystack = [a.name, a.sku, a.gtin, this.articleAttributes(a)]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                return haystack.includes(term)
            })
        },
        // Sichtbare (nicht gelöschte) Bild-Medien, nach Position sortiert.
        visibleMedia() {
            return _.sortBy(
                (this.product?.media || []).filter(m => m.type === 'image' && !m.deleted),
                m => _.get(m, 'position', 0)
            )
        },
    },
    mounted() {
        this.loadEntity()
    },
    watch: {
        // Beim Wechsel zwischen Produkt-/Artikel-Detail (gleiche Komponente) neu laden.
        '$route'() { this.loadEntity() },
    },
    methods: {
        // silent=true: ohne Loading-Skeleton nachladen. Wichtig nach KI-Generierung —
        // sonst würde der v-if-Block (samt offenem Editor-Drawer) aus-/neu gemountet und
        // der Drawer mit veralteter source-image-id leer dargestellt.
        async loadEntity(silent = false) {
            if (!silent) {
                this.loading = true
                this.sortMode = false
                this.articles = []
                this.articleSearch = ''
            }
            try {
                const entity = this.isArticle
                    ? await ArticleApi.fetchArticle(this.$route.params.id)
                    : await ProductApi.fetchProduct({ id: this.$route.params.id })
                // media ist bei bildlosen Produkten/Artikeln null → immer als Array führen,
                // sonst schlägt das Anhängen neuer Bilder fehl.
                if (entity && !Array.isArray(entity.media)) entity.media = []
                this.product = entity
            } catch {
                if (!silent) this.product = null
            } finally {
                if (!silent) this.loading = false
            }
            if (!silent && !this.isArticle && this.product) this.loadArticles()
        },
        // Artikel (Varianten) eines Produkts laden – für die Liste im Produkt-Detail.
        async loadArticles() {
            this.articles = []
            this.articlesLoading = true
            try {
                const list = await ArticleApi.fetchScanArticles({ product: parseInt(this.product.id) })
                this.articles = list || []
            } catch {
                this.articles = []
            } finally {
                this.articlesLoading = false
            }
        },
        // Attribute eines Artikels als Label, z.B. "Größe - L, Farbe - Navy".
        articleAttributes(article) {
            return (article.propertyPivots || [])
                .map(p => {
                    const name = _.get(p, 'property.name')
                    const field = _.get(p, 'property.propType.value_field') || 'text'
                    let val = _.get(p, `valueOption.${field}`)
                    if (val == null || val === '') val = _.get(p, 'valueOption.text')
                    if (val == null || val === '') return null
                    return name ? `${name} - ${val}` : String(val)
                })
                .filter(Boolean)
                .join(', ')
        },
        articleImages(article) {
            return (article.media || []).filter(m => m.type === 'image' && !_.get(m, 'settings.show.disabled'))
        },
        articleImageCount(article) {
            return this.articleImages(article).length
        },
        articleThumb(article) {
            const first = _.sortBy(this.articleImages(article), m => _.get(m, 'position', 0))[0]
            return this.mediaUrl(first)
        },
        goToArticle(article) {
            this.$router.push({ name: 'scan-article', params: { id: article.id } })
        },
        goToParentProduct() {
            if (this.parentProductId) {
                this.$router.push({ name: 'scan-product', params: { id: this.parentProductId } })
            }
        },
        // Aktive Pixel: processedFile → aktive Variante → Source-URL (Product.gql liefert url).
        mediaUrl(media) {
            return _.get(media, 'image.processedFile.url')
                || _.get(media, 'image.activeVariant.file.url')
                || _.get(media, 'image.source.url')
                || null
        },
        openCamera() { this.$refs.cameraInput.click() },
        openGallery() { this.$refs.galleryInput.click() },
        async onFilesPicked(event) {
            const input = event.target
            const files = Array.from(input.files || [])
            input.value = '' // erlaubt erneute Auswahl derselben Datei
            if (!files.length) return

            const newMedia = []
            for (const file of files) {
                if (!this.validateFile(file)) continue
                this.uploading++
                try {
                    const response = await this.uploadFile(file)
                    newMedia.push(this.buildImageMedia(response, (this.product.media || []).length + newMedia.length))
                } catch (err) {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error('[Katalog Produktbilder] Upload fehlgeschlagen:', err)
                    this.notifyError(file.name, err)
                } finally {
                    this.uploading--
                }
            }

            if (newMedia.length) await this.persistMedia([...(this.product.media || []), ...newMedia])
        },
        validateFile(file) {
            if (file.size / 1024 / 1024 > 50) {
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: '50mb' }),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return false
            }
            return true
        },
        // Upload per XHR — exakt wie src/components/Partials/Upload.vue (bewährter Pfad).
        // fetch lieferte hier HTTP 405 (vermutlich Redirect-Downgrade POST→GET); XHR nicht.
        async uploadFile(file) {
            const token = await ensureFreshToken()

            return new Promise((resolve, reject) => {
                const form = new FormData()
                form.append('file', file)
                form.append('context[type]', this.subjectType)

                const xhr = new XMLHttpRequest()
                xhr.onerror = () => reject(new Error('Upload failed'))
                xhr.onload = () => {
                    if (xhr.status < 200 || xhr.status >= 300) {
                        return reject(new Error(`Upload HTTP ${xhr.status}: ${(xhr.responseText || '').slice(0, 200)}`))
                    }
                    try {
                        resolve(JSON.parse(xhr.responseText))
                    } catch (e) {
                        reject(e)
                    }
                }

                xhr.open('POST', this.uploadEndpoint, true)
                xhr.setRequestHeader('authorization', `Bearer ${token}`)
                xhr.setRequestHeader('accept', 'application/json')
                xhr.send(form)
            })
        },
        // Media-Eintrag analog Product/Form/Main.vue uploadSucceed.
        buildImageMedia(response, position) {
            const mime = response.mime
            const type = IMAGE_MIMES.includes(mime) ? 'image' : 'file'
            const hasImage = (this.product.media || []).some(m => m.type === 'image' && !m.deleted)

            const media = {
                id: uniqid('new:'),
                type,
                scope: this.subjectType,
                position: position + 1,
                settings: {
                    show: {
                        list: !hasImage && type === 'image',
                        detail: true,
                        highlight: !hasImage && type === 'image',
                        disabled: false,
                    },
                },
            }

            const source = {
                id: response.id,
                name: response.name,
                preferred_name: response.preferred_name,
                mime,
                type: 'original',
                upload: true,
            }

            if (type === 'image') {
                media.image = {
                    id: uniqid('new:'),
                    width: _.get(response, 'dimensions.width'),
                    height: _.get(response, 'dimensions.height'),
                    source,
                }
            } else {
                media.file = source
            }

            return media
        },
        // Bild aus der Mediathek übernehmen — gleiche Nutzlast wie Main.vue.onProductLibrarySelected.
        async onLibrarySelected(selection) {
            const images = (Array.isArray(selection) ? selection : [selection]).filter(Boolean)
            if (!images.length) return

            const current = this.product.media || []
            const additions = images.map((image, index) => {
                const hasImage = current.some(m => m.type === 'image' && !m.deleted)
                return {
                    id: uniqid('new:'),
                    type: 'image',
                    scope: this.subjectType,
                    position: current.length + index + 1,
                    settings: { show: { list: !hasImage, detail: true, highlight: !hasImage, disabled: false } },
                    image: { ...image, type: 'product-image' },
                }
            })

            await this.persistMedia([...current, ...additions])
        },
        async removeMedia(media) {
            const next = (this.product.media || []).map(m =>
                m.id === media.id ? { ...m, deleted: true } : m
            )
            await this.persistMedia(next)
        },
        // Klick auf eine Bild-Kachel öffnet den Editor (Crop + KI-Tools + Varianten) —
        // wie im Desktop ungated; die KI-Tools schalten sich im Drawer über aiEnabled zu.
        onTileClick(media) {
            if (this.busy || this.sortMode) return
            const imageId = _.get(media, 'image.id')
            if (imageId) this.openEditImage(imageId)
        },
        hasAnyRole(media) {
            return this.roleActive(media, 'highlight')
                || this.roleActive(media, 'list')
                || this.roleActive(media, 'detail')
        },
        // Bild im Sortier-Modus eine Position nach vorne (-1) oder hinten (+1) schieben.
        // Neue Reihenfolge → fortlaufende position-Werte → sofort speichern.
        moveImage(media, dir) {
            if (this.busy) return
            const imgs = this.visibleMedia
            const idx = imgs.findIndex(m => m.id === media.id)
            const target = idx + dir
            if (idx < 0 || target < 0 || target >= imgs.length) return
            const order = [...imgs]
            order.splice(idx, 1)
            order.splice(target, 0, media)
            order.forEach((m, i) => {
                const ref = (this.product.media || []).find(x => x.id === m.id)
                if (ref) this.$set(ref, 'position', i + 1)
            })
            this.persistMedia(this.product.media)
        },
        openEditImage(imageId) {
            this.imageEditSourceId = imageId
            this.showImageDrawer = true
        },
        roleActive(media, scope) {
            return _.get(media, `settings.show.${scope}`) === true
        },
        // Rollen-Umschaltung aus dem Editor-Overlay: betrifft das gerade geöffnete Bild.
        onToggleRole(scope) {
            const media = (this.product.media || []).find(
                m => String(_.get(m, 'image.id')) === String(this.imageEditSourceId)
            )
            if (media) this.toggleRole(media, scope)
        },
        // Bild-Rolle umschalten (wie im Desktop-Produktformular):
        // - highlight/list: Einzelauswahl – genau ein Bild; Klick aktiviert dieses, andere aus.
        // - detail: mehrfach, aber mindestens eines muss aktiv bleiben.
        toggleRole(media, scope) {
            if (this.busy) return
            const list = this.product.media || []
            const setShow = (m, value) => {
                if (!m.settings) this.$set(m, 'settings', {})
                if (!m.settings.show) this.$set(m.settings, 'show', {})
                this.$set(m.settings.show, scope, value)
            }
            if (scope === 'highlight' || scope === 'list') {
                list.forEach(m => { if (m.type === 'image') setShow(m, false) })
                setShow(media, true)
            } else if (scope === 'detail') {
                const active = this.roleActive(media, 'detail')
                if (!active) {
                    setShow(media, true)
                } else if (list.filter(m => this.roleActive(m, 'detail')).length > 1) {
                    setShow(media, false)
                }
                // sonst: aktiv lassen (mindestens eines erforderlich)
            }
            this.persistMedia(list)
        },
        // --- Rückspielung der Editor-Ergebnisse (analog Main.vue) ---
        // Persistierte Bild-Mutation (AI/Crop): Server hat bereits gespeichert → neu laden.
        onImageUpdated(image, previousId = null) {
            if (!image) return
            const matchId = previousId != null ? previousId : _.get(image, 'id')
            const media = (this.product.media || []).find(item => String(_.get(item, 'image.id')) === String(matchId))
            const wasUnsaved = String(matchId || '').startsWith('new:') || String(_.get(image, 'id') || '').startsWith('new:')
            if (media && media.image && wasUnsaved) {
                const keys = ['id', 'source', 'settings', 'processing_query_string', 'processedFile', 'activeVariant', 'variants', 'alt', 'width', 'height', 'url', 'upload', 'ai_disclosure', 'ai_source_type', 'ai_label_display', 'ai_assessment']
                keys.forEach(key => this.$set(media.image, key, _.get(image, key)))
                this.persistMedia(this.product.media)
                return
            }
            this.onGenerationCompleted()
        },
        // Bild ersetzt (Upload/Library im Editor) → image-Objekt tauschen und sofort sichern.
        onImageReplaced(image, previousId) {
            if (!image) return
            const media = (this.product.media || []).find(item => String(_.get(item, 'image.id')) === String(previousId))
            if (media) {
                this.$set(media, 'image', image)
                this.persistMedia(this.product.media)
            }
        },
        // Alt-Text aus dem Editor → ins media-Array und sichern.
        onImageAltUpdated(imageId, alt) {
            const media = (this.product.media || []).find(item => String(_.get(item, 'image.id')) === String(imageId))
            if (media && media.image) {
                this.$set(media.image, 'alt', alt)
                this.persistMedia(this.product.media)
            }
        },
        // Art. 50(4) disclosure decisions. This view has no surrounding Save, so it
        // persists straight away like alt does here.
        onImageAiDisclosureUpdated(imageId, disclose) {
            const media = (this.product.media || []).find(item => String(_.get(item, 'image.id')) === String(imageId))
            if (media && media.image) {
                this.$set(media.image, 'ai_disclosure', disclose === true)
                this.persistMedia(this.product.media)
            }
        },
        onImageAiLabelDisplayUpdated(imageId, mode) {
            const media = (this.product.media || []).find(item => String(_.get(item, 'image.id')) === String(imageId))
            if (media && media.image) {
                this.$set(media.image, 'ai_label_display', mode || null)
                this.persistMedia(this.product.media)
            }
        },
        onImageAiSourceTypeUpdated(imageId, sourceType) {
            const media = (this.product.media || []).find(item => String(_.get(item, 'image.id')) === String(imageId))
            if (media && media.image) {
                this.$set(media.image, 'ai_source_type', sourceType || null)
                this.persistMedia(this.product.media)
            }
        },
        // AI-Generierung fertig → Entität STILL neu laden (kein Skeleton, damit der offene
        // Editor-Drawer nicht aus-/neu gemountet wird und leer erscheint).
        onGenerationCompleted() {
            this.loadEntity(true)
        },
        // Komplettes Media-Set sofort per Update (Produkt bzw. Artikel) speichern und mit
        // der Server-Antwort (enthält echte IDs + URLs) ersetzen.
        async persistMedia(media) {
            this.saving = true
            try {
                const payload = { id: parseInt(this.product.id), media }
                const updated = this.isArticle
                    ? await ArticleApi.update(payload)
                    : await ProductApi.update(payload)
                this.product = { ...this.product, media: (updated && updated.media) || [] }
            } catch (err) {
                /* eslint no-console: ["error", { allow: ["error"] }] */
                console.error('[Katalog Produktbilder] Speichern fehlgeschlagen:', err)
                this.notifyError(null, err)
            } finally {
                this.saving = false
            }
        },
        notifyError(name, err) {
            const detail = err && (err.message || String(err))
            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: [name, detail].filter(Boolean).join(' – '),
                iconClass: 'fas el-icon-fa-xmark',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            })
        },
    },
}
</script>

<style lang="sass" scoped>
.scan-product
    flex: 1
    min-height: 0
    overflow-y: auto
    width: 100%
    padding: 16px
    max-width: 480px
    margin: 0 auto

.page-loading
    padding: 16px
    ::v-deep
        .el-skeleton__item
            background: #1a1a1a

.scan-product__backlink.el-button
    display: flex
    align-items: center
    justify-content: center
    width: 100%
    min-height: 44px
    margin: 0 0 14px
    border-radius: 8px
    background: #1a1a1a
    border-color: #2a2a2a
    color: $--color-primary
    font-size: 14px
    &:hover,
    &:focus
        background: #1f1f1f
        border-color: $--color-primary
        color: $--color-primary

.scan-product__head
    margin-bottom: 16px

    .scan-product__name
        font-size: 18px
        font-weight: 700
        color: #d0d0d0
        margin-bottom: 4px

    .scan-product__nr
        font-size: 13px
        color: #777

.scan-product__galleryhead
    display: flex
    justify-content: flex-end
    margin-bottom: 8px

    ::v-deep .el-button--text
        color: $--color-primary
        padding: 2px 0
        i
            margin-right: 4px

.scan-product__gallery
    display: flex
    flex-wrap: wrap
    gap: 8px
    margin-bottom: 24px

    // Sortier-Modus: eine Reihe pro Bild über die ganze Breite
    &.sorting
        flex-direction: column

        .media-tile
            width: 100%
            aspect-ratio: auto
            height: 72px

.media-tile
    position: relative
    width: calc(33.333% - 6px)
    aspect-ratio: 1 / 1
    border-radius: 8px
    overflow: hidden
    background: #0f0f0f
    border: 1px solid #2a2a2a
    display: flex
    align-items: center
    justify-content: center

    img
        width: 100%
        height: 100%
        object-fit: cover

    > i
        font-size: 24px
        color: #444

    &--uploading > i
        color: $--color-primary

    &--editable
        cursor: pointer

    // Kleiner KI-Hinweis oben links (Antippen der Kachel öffnet den Editor)
    .media-tile__ai
        position: absolute
        top: 4px
        left: 4px
        width: 22px
        height: 22px
        border-radius: 6px
        background: rgba(0, 0, 0, 0.55)
        display: flex
        align-items: center
        justify-content: center
        pointer-events: none
        i
            font-size: 11px
            color: #b388ff

    .media-tile__delete
        position: absolute
        top: 4px
        right: 4px
        width: 22px
        height: 22px
        border: none
        border-radius: 6px
        background: rgba(0, 0, 0, 0.55)
        color: #fff
        cursor: pointer
        display: flex
        align-items: center
        justify-content: center
        -webkit-tap-highlight-color: transparent

        i
            font-size: 11px

        &:disabled
            opacity: 0.4

    // Rollen-Anzeige (read-only) als vollbreite Leiste am unteren Bildrand
    .media-tile__rolebadges
        position: absolute
        left: 0
        right: 0
        bottom: 0
        display: flex
        gap: 8px
        align-items: center
        justify-content: center
        padding: 4px 6px
        background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0))
        pointer-events: none
        i
            font-size: 11px
            color: $--color-primary

    // Sortier-Modus: Hoch/Runter-Pfeile rechts in der vollbreiten Reihe
    .media-tile__move
        position: absolute
        right: 8px
        top: 50%
        transform: translateY(-50%)
        display: flex
        gap: 8px

        .move-btn
            width: 38px
            height: 38px
            border: none
            border-radius: 8px
            background: rgba(0, 0, 0, 0.55)
            color: #fff
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            -webkit-tap-highlight-color: transparent
            i
                font-size: 16px
            &:disabled
                opacity: 0.3
                cursor: default

.scan-product__empty
    width: 100%
    padding: 32px 0
    display: flex
    flex-direction: column
    align-items: center
    gap: 8px
    color: #555

    i
        font-size: 32px

.scan-product__actions
    display: flex
    flex-direction: column
    gap: 10px

    .hidden-input
        display: none

    ::v-deep .action-btn
        width: 100%
        margin: 0

        &:not(.el-button--primary)
            background: #1a1a1a
            border-color: #2a2a2a
            color: #d0d0d0

.scan-product__articles
    margin-top: 8px

    .scan-product__articles-title
        font-size: 13px
        font-weight: 600
        color: #888
        text-transform: uppercase
        letter-spacing: 0.04em
        margin-bottom: 8px

    .articles-loading
        padding: 4px 0
        ::v-deep .el-skeleton__item
            background: #1a1a1a

    .scan-product__articles-search
        margin-bottom: 10px
        ::v-deep
            .el-input__inner
                background: #1a1a1a
                border-color: #2a2a2a
                color: #d0d0d0
            .el-input__icon
                color: #666

    .article-row
        display: flex
        align-items: center
        gap: 10px
        padding: 8px
        margin-bottom: 8px
        border-radius: 8px
        background: #1a1a1a
        border: 1px solid #2a2a2a
        cursor: pointer
        -webkit-tap-highlight-color: transparent

        .article-thumb
            width: 44px
            height: 44px
            flex-shrink: 0
            border-radius: 6px
            overflow: hidden
            background: #0f0f0f
            border: 1px solid #2a2a2a
            display: flex
            align-items: center
            justify-content: center
            img
                width: 100%
                height: 100%
                object-fit: cover
            i
                font-size: 18px
                color: #444

        .article-info
            flex: 1
            min-width: 0
            .article-name
                font-size: 14px
                color: #d0d0d0
                white-space: nowrap
                overflow: hidden
                text-overflow: ellipsis
            .article-sku
                font-size: 12px
                color: #777

        .article-count
            font-size: 12px
            color: $--color-primary
            flex-shrink: 0
            i
                margin-right: 3px

        .arrow
            color: #333
            font-size: 13px
            flex-shrink: 0

::v-deep .el-empty
    &__description p
        color: #666
</style>

<!-- Mobile-Optimierung des KI-Editor-Drawers. Der Drawer wird an document.body gehängt
     (append-to-body) und ist von Haus aus ein reines Desktop-Layout (1040px, Canvas +
     fixe 300px-Tool-Spalte nebeneinander, keine @media-Regeln). Diese Overrides sind
     unscoped (scoped Styles erreichen den Drawer nicht) und mit !important, um die
     scoped Regeln der Drawer-Komponente zu überschreiben. Greifen nur ≤768px, betreffen
     also ausschließlich Mobil/Schmal – das Desktop-Produktformular bleibt unberührt. -->
<style lang="sass">
@media (max-width: 768px)
    // Drawer auf volle Breite (statt fixe 1040px von rechts)
    .el-drawer.full-height-drawer
        width: 100% !important

    // Editor mit reduziertem horizontalem Padding (statt des Desktop-Wertes), damit
    // Canvas + Tools mehr Breite bekommen, aber ein kleiner Rand bleibt.
    #body .full-height-drawer .drawer-body .block-content,
    #app .full-height-drawer .drawer-body .block-content
        padding-left: 14px !important
        padding-right: 14px !important

    .gm-editor-drawer
        .drawer-body
            padding: 12px 0 !important

        // Canvas-Spalte und Tool-Spalte untereinander statt nebeneinander
        .gm-editor
            gap: 12px !important

        .gm-editor__main,
        .gm-editor__rail
            flex: 1 1 100% !important
            width: 100% !important
            min-width: 0 !important

        // "Zuschneiden"-Tool im Mobil-Editor ausblenden (nur KI-Bearbeitung gewünscht)
        .gm-tool--crop
            display: none !important

        // Toolbar darf bei wenig Platz umbrechen
        .gm-toolbar
            flex-wrap: wrap !important

        .gm-editor__canvas
            padding: 8px !important

        // Footer-Buttons über volle Breite gut tappbar
        .drawer-footer
            flex-wrap: wrap !important

    // Mediathek-Dialog mobil: feste 1200px-Breite (Inline-Style) auf (fast) volle Breite
    // zwingen, sonst läuft der Dialog über den Bildschirm. Grid kompakter (mehr Spalten),
    // Sidebar stapelt bereits über die komponenteneigenen @media-Regeln.
    .el-dialog.image-library-dialog
        width: 96% !important
        max-width: 96% !important
        margin-left: auto !important
        margin-right: auto !important

    .image-library-dialog
        .el-dialog__body
            padding: 0 12px 12px !important
        .image-library__grid
            grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)) !important
</style>
